import React, { useEffect, useState, useCallback } from "react";
import { ArtworkPreviewList } from "./workspace/ArtworkPreview";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { GrossBidNotice } from "./workspace/GrossBid";
import BidCalculatorPage from "./workspace/BidCalculator";
import { PerUnit } from "./workspace/shared";
import {
  ArrowRight, Package, Clock3, MapPin, IndianRupee, CheckCircle2,
  CircleDollarSign, Loader2, LogOut, RefreshCw,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
axios.defaults.withCredentials = true;

/**
 * /materials — Material Supplier dashboard.
 *
 * Lists live sub-RFQs from printers, lets the supplier submit (and re-submit)
 * sealed bids, and shows which RFQs they've already quoted on.
 */
export default function Materials() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState("live"); // live | mine | awarded
  const [rfqs, setRfqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [activeRfqId, setActiveRfqId] = useState(null);

  const bootstrap = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/auth/me`);
      if (!["material_supplier", "finishing_partner", "admin"].includes(r.data?.role)) {
        navigate("/login");
        return;
      }
      setMe(r.data);
    } catch {
      navigate("/login");
    }
  }, [navigate]);

  const fetchRfqs = useCallback(async () => {
    setLoading(true); setErr("");
    try {
      const params = tab === "mine" ? { mine: true } : tab === "awarded" ? { status: "awarded" } : {};
      const r = await axios.get(`${API}/material-rfqs`, { params });
      setRfqs(r.data?.rfqs || []);
    } catch (e) {
      setErr(e.response?.data?.detail || "Could not load RFQs.");
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { bootstrap(); }, [bootstrap]);
  useEffect(() => { if (me) fetchRfqs(); }, [me, fetchRfqs]);

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch {}
    navigate("/login");
  };

  if (!me) return <div className="mat-boot"><Loader2 className="mat-spin"/> Loading…</div>;

  return (
    <div className="mat-shell" data-testid="materials-page">
      <header className="mat-nav">
        <Link to="/" className="m-brand"><img src="/logo.png" alt="GoPrintPack" className="m-brand-logo"/></Link>
        <div className="mat-nav-mid">
          <span className="mat-chip" data-testid="materials-role-chip">{me.role === "finishing_partner" ? "Finishing Partner" : "Material Supplier"}</span>
          <h1 className="mat-nav-title">Welcome back, {me.name}</h1>
        </div>
        <div className="mat-nav-actions">
          <Link to="/workspace" className="m-ghost" data-testid="materials-workspace-link">Plans, payouts & help</Link>
          <button className="m-ghost" onClick={fetchRfqs} data-testid="materials-refresh">
            <RefreshCw size={14}/> Refresh
          </button>
          <button className="m-ghost" onClick={logout} data-testid="materials-logout">
            <LogOut size={14}/> Sign out
          </button>
        </div>
      </header>

      <nav className="mat-tabs" role="tablist">
        {[
          { id: "live",    label: "Live RFQs" },
          { id: "mine",    label: "My quotes" },
          { id: "awarded", label: "Won" },
          { id: "calculator", label: "Bid Calculator & Fee Guide" },
        ].map(t => (
          <button key={t.id} role="tab"
                  className={`mat-tab ${tab === t.id ? "mat-tab-on" : ""}`}
                  onClick={() => setTab(t.id)}
                  data-testid={`materials-tab-${t.id}`}>
            {t.label}
          </button>
        ))}
      </nav>

      {err && <div className="mat-err">{err}</div>}
      {tab === "calculator"
        ? <div style={{maxWidth:1200,margin:"0 auto",padding:"20px 36px 40px"}}><BidCalculatorPage/></div>
        : loading
        ? <div className="mat-boot"><Loader2 className="mat-spin"/> Loading RFQs…</div>
        : rfqs.length === 0
          ? <EmptyState tab={tab}/>
          : (
            <section className="mat-list" data-testid="materials-list">
              {rfqs.map(r => (
                <RfqCard
                  key={r.id} rfq={r} supplierId={me.id}
                  isOpen={activeRfqId === r.id}
                  onToggle={() => setActiveRfqId(x => x === r.id ? null : r.id)}
                  onBidPlaced={fetchRfqs}
                />
              ))}
            </section>
          )
      }

      <footer className="mat-foot">
        Have a question? <a href="mailto:info@goprintpack.com">info@goprintpack.com</a>
      </footer>
    </div>
  );
}

/* ------- RFQ card + inline bid form ------- */
export function RfqCard({ rfq, supplierId, isOpen, onToggle, onBidPlaced }) {
  const [bid, setBid] = useState({ price: "", turnaround_days: "", note: "" });
  const [myBid, setMyBid] = useState(null);
  const [status, setStatus] = useState({ busy: false, err: "", ok: "" });

  // Load this RFQ in detail (lazy) when the card expands — gives us the current bid
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/material-rfqs/${rfq.id}`);
        const mine = (r.data?.bids || []).find(b => b.supplier_id === supplierId);
        if (mine) {
          setMyBid(mine);
          setBid({ price: String(mine.price), turnaround_days: String(mine.turnaround_days), note: mine.note || "" });
        }
      } catch {}
    })();
  }, [isOpen, rfq.id, supplierId]);

  const submitBid = async (e) => {
    e.preventDefault();
    setStatus({ busy: true, err: "", ok: "" });
    try {
      const r = await axios.post(`${API}/material-rfqs/${rfq.id}/bids`, {
        price: Number(bid.price),
        turnaround_days: Number(bid.turnaround_days),
        note: bid.note || null,
      });
      setMyBid(r.data);
      setStatus({ busy: false, err: "", ok: myBid ? "Bid updated." : "Bid submitted. The printer will review and award." });
      onBidPlaced?.();
    } catch (e2) {
      setStatus({ busy: false, err: e2.response?.data?.detail || "Could not submit bid.", ok: "" });
    }
  };

  const won = !!rfq.awarded_supplier_id && rfq.awarded_supplier_id === supplierId;
  const [ship, setShip] = useState({ carrier: "", tracking_number: "" });
  const markDispatched = async (e) => {
    e.preventDefault();
    setStatus({ busy: true, err: "", ok: "" });
    try {
      await axios.post(`${API}/material-rfqs/${rfq.id}/dispatch`, ship);
      setStatus({ busy: false, err: "", ok: "Marked dispatched. Escrow releases once the printer confirms receipt." });
      onBidPlaced?.();
    } catch (e2) {
      setStatus({ busy: false, err: e2.response?.data?.detail || "Could not mark dispatched.", ok: "" });
    }
  };

  return (
    <article className={`mat-card ${won ? "mat-card-won" : ""}`} data-testid={`materials-rfq-${rfq.id}`}>
      <div className="mat-card-head" onClick={onToggle}>
        <div className="mat-card-icon"><Package size={18}/></div>
        <div className="mat-card-meta">
          <div className="mat-card-cat">{humanCategory(rfq.material_category)}</div>
          <div className="mat-card-desc">{rfq.description}</div>
          <div className="mat-card-tags">
            {rfq.expected_price_hint ? <span><IndianRupee size={11}/> ₹{rfq.expected_price_hint.toLocaleString("en-IN")} target</span> : null}
            <span><Clock3 size={11}/> {rfq.deadline_days}d deadline</span>
            <span><MapPin size={11}/> {rfq.ship_to_pincode}</span>
            <span>Qty: <b>{rfq.quantity} {rfq.quantity_unit}</b></span>
            <span className="mat-card-bidcount">{rfq.bid_count || 0} bids</span>
            {rfq.source === "own" && <span data-testid={`materials-own-${rfq.id}`} style={{background:"#FEF3C7",color:"#92400E",fontWeight:700}}>Direct printer order</span>}
            {rfq.matches_me && <span data-testid={`materials-match-${rfq.id}`} style={{background:"#ECFDF5",color:"#047857",border:"1px solid #6EE7B7",fontWeight:700}}>✓ Matches you</span>}
          </div>
        </div>
        <div className="mat-card-cta">
          {won
            ? <span className="mat-wonchip" data-testid={`materials-won-${rfq.id}`}><CheckCircle2 size={13}/> You won · ₹{rfq.awarded_price?.toLocaleString("en-IN")} · {WON_LABEL[rfq.status] || rfq.status}</span>
            : rfq.status !== "live"
              ? <span className="mat-closed">Awarded</span>
              : <span className="mat-open">{isOpen ? "Close" : (myBid ? "Edit quote" : "Submit quote")} <ArrowRight size={13}/></span>}
        </div>
      </div>

      {won && rfq.ship_to_address && ["awarded", "dispatched"].includes(rfq.status) && (
        <div className="mat-ok" data-testid={`materials-shipto-${rfq.id}`} style={{margin:"0 16px 10px"}}>
          <b>Ship to:</b> {[rfq.ship_to_address.contact_name, rfq.ship_to_address.line1, rfq.ship_to_address.line2, rfq.ship_to_address.city, rfq.ship_to_address.state, rfq.ship_to_address.pincode].filter(Boolean).join(", ")}
          {rfq.ship_to_address.contact_phone ? ` · ${rfq.ship_to_address.contact_phone}` : ""}
        </div>
      )}
      {won && rfq.status === "awarded" && (
        <form className="mat-bid-form" onSubmit={markDispatched} data-testid={`materials-dispatch-form-${rfq.id}`}>
          {status.err && <div className="mat-err">{status.err}</div>}
          {status.ok && <div className="mat-ok">{status.ok}</div>}
          <div className="mat-bid-row">
            <label><span>Carrier</span>
              <input value={ship.carrier} onChange={e => setShip({ ...ship, carrier: e.target.value })} data-testid={`materials-dispatch-carrier-${rfq.id}`} placeholder="DTDC / own vehicle"/>
            </label>
            <label><span>Tracking / LR number</span>
              <input value={ship.tracking_number} onChange={e => setShip({ ...ship, tracking_number: e.target.value })} data-testid={`materials-dispatch-tracking-${rfq.id}`}/>
            </label>
          </div>
          <button type="submit" className="m-cta" disabled={status.busy} data-testid={`materials-dispatch-submit-${rfq.id}`}>
            {status.busy ? "Saving…" : <>Mark dispatched <ArrowRight size={13}/></>}
          </button>
        </form>
      )}
      {won && rfq.status === "received" && (
        <div className="mat-ok" data-testid={`materials-payout-${rfq.id}`} style={{margin:"0 16px 14px"}}>
          Printer confirmed receipt. Payout ₹{rfq.payout_inr?.toLocaleString("en-IN")} (quote + your GST − {rfq.take_rate_pct}% platform fee − 18% GST on fee − 1% TDS) · {rfq.payout_status === "manual_paid" || rfq.payout_status === "released" ? "paid" : "processing"}
          {rfq.invoice_supplier_id && (
            <> · <a href={`${API}/invoices/${rfq.invoice_supplier_id}/pdf`} target="_blank" rel="noreferrer" data-testid={`materials-invoice-${rfq.id}`}>Invoice {rfq.invoice_supplier_number}</a></>
          )}
        </div>
      )}
      {isOpen && rfq.job_id && <div style={{ padding: "0 16px" }}><ArtworkPreviewList rfqId={rfq.id}/></div>}
      {isOpen && rfq.status === "live" && (
        <form className="mat-bid-form" onSubmit={submitBid} data-testid={`materials-bid-form-${rfq.id}`}>
          {status.err && <div className="mat-err">{status.err}</div>}
          {status.ok && <div className="mat-ok">{status.ok}</div>}
          <div className="mat-bid-row">
            <label>
              <span>Gross price (₹, incl. our commission) *</span>
              <input type="number" min="1" step="any" required value={bid.price}
                     onChange={e => setBid({ ...bid, price: e.target.value })}
                     data-testid={`materials-bid-price-${rfq.id}`} placeholder="42000"/>
              <PerUnit price={bid.price} qty={rfq.quantity} unit={rfq.quantity_unit} testid={`materials-bid-per-unit-${rfq.id}`}/>
            </label>
            <label>
              <span>Turnaround (days) *</span>
              <input type="number" min="1" max="90" required value={bid.turnaround_days}
                     onChange={e => setBid({ ...bid, turnaround_days: e.target.value })}
                     data-testid={`materials-bid-days-${rfq.id}`} placeholder="3"/>
            </label>
          </div>
          <GrossBidNotice amount={bid.price} label="quote" gstRate={rfq.kind === "finishing" ? 5 : 18}/>
          <label className="mat-bid-note">
            <span>Note (optional)</span>
            <textarea rows={2} maxLength={600} value={bid.note}
                      onChange={e => setBid({ ...bid, note: e.target.value })}
                      data-testid={`materials-bid-note-${rfq.id}`}
                      placeholder="Brand carried, stock status, any delivery nuance..."/>
          </label>
          <button type="submit" className="m-cta" disabled={status.busy}
                  data-testid={`materials-bid-submit-${rfq.id}`}>
            {status.busy
              ? "Submitting..."
              : <>{myBid ? "Update bid" : "Submit sealed bid"} <ArrowRight size={13}/></>}
          </button>
        </form>
      )}
    </article>
  );
}

function EmptyState({ tab }) {
  const msg = tab === "live"
    ? "No live RFQs right now. New requests from printers will appear here as they come in."
    : tab === "mine"
      ? "You haven't quoted on any RFQs yet. Head to Live RFQs to place your first bid."
      : "No awards yet. Keep quoting — your wins will show up here.";
  return (
    <div className="mat-empty" data-testid="materials-empty">
      <CircleDollarSign size={28}/>
      <p>{msg}</p>
    </div>
  );
}

const WON_LABEL = { awarded: "escrow funded · ship now", dispatched: "dispatched", received: "received" };

export function humanCategory(c) {
  const map = {
    paper: "Paper", board: "Board", flexo_film: "Flexo film", ink: "Ink / coating",
    plate: "Plate / chemical", adhesive: "Adhesive", other: "Other",
    lamination: "Lamination", foiling: "Foiling", embossing: "Embossing / debossing", spot_uv: "Spot UV",
    die_cutting: "Die-cutting", binding: "Binding", window_patching: "Window patching",
  };
  return map[c] || c;
}
